import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, Platform, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import RNPrint from "react-native-print";
import { ZenButton, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast, ZenToastConfig, ZenWebView } from "zen-core-elements";

import { CustomModal } from "../../../shared/commonComponents/customModal";
import { useAppSelector } from "../../../redux/hooks";
import useProductSettings from "../../../shared/hooks/useProductSettings";
import { getLabelLayout } from "../../settings/productSettings.module";
import {
    collectLabelRows,
    flattenLabelRows,
    LABEL_QUANTITY_MAX,
    LABEL_QUANTITY_MIN,
    LabelRow,
    labelRowKey,
    parseLabelQuantity,
} from "../../../utils/productLabel";
import {
    buildLabelSheetHtml,
    generateLabelSheetPdf,
    LabelPdfResult,
    openLabelSheetPdf,
    SavedLabelSheet,
    saveLabelSheetPdf,
} from "../../../utils/productLabelSheet";
import { ProductDataProps } from "./productModule";
import CommonHeader from "../../../shared/commonComponents/commonHeader";
import Toast from "react-native-toast-message";

interface PrintProductLabelsProps {
    visible: boolean;
    onClose: () => void;
    products: ProductDataProps[];
    selectedIds: number[];
}

const QUANTITY_ERROR = `1 - ${LABEL_QUANTITY_MAX}`;

interface QuantityRowProps {
    row: LabelRow;
    value: string;
    disabled: boolean;
    theme: Theme;
    onChange: (key: string, text: string) => void;
}

/** One printable product in the modal, with its own quantity field. */
const QuantityRow = React.memo(({ row, value, disabled, theme, onChange }: QuantityRowProps) => {
    const style = theme?.printProductLabelsStyle;
    const invalid = parseLabelQuantity(value) === null;
    const key = labelRowKey(row);

    return (
        <View style={[style?.productRow, invalid && style?.productRowInvalid]}>
            <View style={style?.productInfo}>
                <ZenText
                    content={row.productName || "Product"}
                    size={theme?.smallerFontSize}
                    color={theme?.lightDark}
                    bold
                    numberOfLines={1}
                />
                {Boolean(row.variantName) && (
                    <ZenText
                        content={String(row.variantName)}
                        size={theme?.smallHeaderFontSize}
                        color={theme?.grey}
                        numberOfLines={1}
                    />
                )}
                <View style={style?.skuChip}>
                    <ZenText content={row.sku} size={theme?.smallHeaderFontSize} color={theme?.dark} />
                </View>
            </View>

            <View style={style?.qtyInputWrapper}>
                <ZenInput
                    label="Qty"
                    value={value}
                    disabled={disabled}
                    editable={!disabled}
                    keyboardType="number-pad"
                    maxLength={3}
                    type="outlined"
                    accessibilityLabel={`Quantity for ${row.productName} ${row.sku}`}
                    errorMessage={invalid ? QUANTITY_ERROR : undefined}
                    errorStyle={style?.errorStyle}
                    labelStyle={style?.inputLabel}
                    containerStyle={style?.qtyInputContainer}
                    inputContainerStyle={style?.qtyInputInner}
                    inputStyle={style?.qtyInputText}
                    onChangeText={(text) => onChange(key, text.replace(/[^0-9]/g, ""))}
                />
            </View>
        </View>
    );
});

const PrintProductLabels = ({ visible, onClose, products, selectedIds }: PrintProductLabelsProps) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const currencySymbol =
        useAppSelector((state) => state.common.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? "";

    // Picks up a layout or field change made in Settings while this screen stayed mounted.
    const { settings } = useProductSettings({ reloadOnFocus: true });

    const [pdf, setPdf] = useState<LabelPdfResult | null>(null);
    const [savedFile, setSavedFile] = useState<SavedLabelSheet | null>(null);
    const [busy, setBusy] = useState<null | "download" | "print">(null);
    const [tab, setTab] = useState<"quantities" | "preview">("quantities");
    const [quantities, setQuantities] = useState<Record<string, string>>({});
    const [bulkQuantity, setBulkQuantity] = useState<string>("");

    const { rows, skippedProductNames } = useMemo(
        () => collectLabelRows(products, selectedIds),
        [products, selectedIds],
    );

    // Every printable row starts at 1; quantities already typed are kept.
    useEffect(() => {
        setQuantities((previous) => {
            const next: Record<string, string> = {};
            rows.forEach((row) => {
                const key = labelRowKey(row);
                next[key] = previous[key] ?? String(LABEL_QUANTITY_MIN);
            });
            return next;
        });
    }, [rows]);

    const onQuantityChange = useCallback((key: string, text: string) => {
        setQuantities((previous) => ({ ...previous, [key]: text }));
    }, []);

    const onApplyToAll = useCallback(() => {
        const parsed = parseLabelQuantity(bulkQuantity);
        if (parsed === null) {
            ZenToast({ message: `Enter a quantity between ${LABEL_QUANTITY_MIN} and ${LABEL_QUANTITY_MAX}`, type: "warning" });
            return;
        }
        setQuantities((previous) =>
            Object.keys(previous).reduce<Record<string, string>>((next, key) => {
                next[key] = String(parsed);
                return next;
            }, {}),
        );
        ZenToast({ message: `Quantity ${parsed} applied to all products`, type: "success" });
    }, [bulkQuantity]);

    // Flatten: one entry per unit, in the order shown in the modal.
    const { flatRows, totalLabels, invalidCount } = useMemo(() => {
        let invalid = 0;
        const items = rows.map((row) => {
            const parsed = parseLabelQuantity(quantities[labelRowKey(row)] ?? "");
            if (parsed === null) {
                invalid += 1;
            }
            return { row, quantity: parsed ?? 0 };
        });
        const flattened = flattenLabelRows(items);
        return { flatRows: flattened, totalLabels: flattened.length, invalidCount: invalid };
    }, [rows, quantities]);

    const html = useMemo(
        () => buildLabelSheetHtml(flatRows, settings, currencySymbol),
        [flatRows, settings, currencySymbol],
    );

    // The cached PDF is only valid for the html that produced it.
    useEffect(() => {
        setPdf(null);
        setSavedFile(null);
    }, [html]);

    const layout = getLabelLayout(settings.label.layout);

    const ensurePdf = useCallback(async () => {
        if (pdf) {
            return pdf;
        }
        const created = await generateLabelSheetPdf(html, settings);
        setPdf(created);
        return created;
    }, [pdf, html, settings]);

    const onDownload = useCallback(async () => {
        setBusy("download");
        try {
            const created = await ensurePdf();

            const saved = await saveLabelSheetPdf(created);
            setSavedFile(saved);

            ZenToast({
                message:
                    saved.location === "notification"
                        ? `${created.fileName} downloaded. Open it from the notification.`
                        : saved.location === "downloads"
                            ? `${created.fileName} saved to Downloads`
                            : `${created.fileName} saved`,
                type: "success",
            });
        } catch (e) {
            console.log("Inside the catch ", e);
            ZenToast({ message: "Unable to save the label PDF", type: "error" });
        } finally {
            setBusy(null);
        }
    }, [ensurePdf]);

    const onOpenSaved = useCallback(async () => {
        if (!savedFile) {
            return;
        }
        try {
            await openLabelSheetPdf(savedFile.path);
        } catch {
            ZenToast({ message: "No app available to open the PDF", type: "warning" });
        }
    }, [savedFile]);

    const onPrint = useCallback(async () => {
        if (busy) {
            return;
        }
        setBusy("print");
        try {
            // One job for the whole flattened list, in modal order.
            await RNPrint.print({ html, jobName: `Product labels (${totalLabels})` });
        } catch {
            ZenToast({ message: "Unable to open the print dialog", type: "error" });
        } finally {
            setBusy(null);
        }
    }, [busy, html, totalLabels]);

    const hasLabels = rows.length > 0;
    const canPrint = hasLabels && invalidCount === 0 && totalLabels > 0 && busy === null;

    const renderRow = useCallback(
        ({ item, theme }: { item: LabelRow; theme: Theme }) => (
            <QuantityRow
                row={item}
                value={quantities[labelRowKey(item)] ?? ""}
                disabled={busy !== null}
                theme={theme}
                onChange={onQuantityChange}
            />
        ),
        [quantities, busy, onQuantityChange],
    );

    return (
        <CustomModal visible={visible} onRequestClose={onClose} animationType="slide" keyboardView>
            <ThemeConsumer>
                {({ theme }: { theme: Theme }) => {
                    const style = theme?.printProductLabelsStyle;
                    return (
                        <View style={style?.container}>
                            <CommonHeader textBadge={<ZenText
                                content={layout.label}
                                size={theme?.smallHeaderFontSize}
                                color={theme?.grey}
                            />}
                                backIcon
                                backIconOnpress={onClose}
                                headerName="Label Printer" />

                            {skippedProductNames.length > 0 && (
                                <View style={style?.warningBar}>
                                    <ZenIcon
                                        name="alert-outline"
                                        type="material-community"
                                        size={theme?.iconSize}
                                        color={theme?.pendingColor}
                                    />
                                    <ZenText
                                        content={`${skippedProductNames.length} product${skippedProductNames.length === 1 ? "" : "s"} skipped: no SKU (${skippedProductNames.slice(0, 3).join(", ")}${skippedProductNames.length > 3 ? ` and ${skippedProductNames.length - 3} more` : ""})`}
                                        size={theme?.smallHeaderFontSize}
                                        color={theme?.lightDark}
                                        style={{ flex: 1 }}
                                    />
                                </View>
                            )}

                            {hasLabels ? (
                                <>
                                    <View style={style?.tabBar}>
                                        {(["quantities", "preview"] as const).map((key) => (
                                            <TouchableOpacity
                                                key={key}
                                                style={[style?.tab, tab === key && style?.tabActive]}
                                                accessibilityRole="tab"
                                                accessibilityState={{ selected: tab === key }}
                                                onPress={() => setTab(key)}
                                            >
                                                <ZenText
                                                    content={key === "quantities" ? "Quantities" : "Preview"}
                                                    size={theme?.smallerFontSize}
                                                    color={tab === key ? theme?.primary : theme?.grey}
                                                    bold={tab === key}
                                                />
                                            </TouchableOpacity>
                                        ))}
                                    </View>

                                    {tab === "quantities" ? (
                                        <>
                                            <View style={style?.bulkCard}>
                                                <ZenText
                                                    content="Set the same quantity for every product"
                                                    size={theme?.smallHeaderFontSize}
                                                    color={theme?.grey}
                                                />
                                                <View style={style?.bulkRow}>
                                                    <View style={style?.bulkInputWrapper}>
                                                        <ZenInput
                                                            value={bulkQuantity}
                                                            disabled={busy !== null}
                                                            editable={busy === null}
                                                            keyboardType="number-pad"
                                                            maxLength={3}
                                                            type="outlined"
                                                            placeholder="Qty"
                                                            accessibilityLabel="Quantity for all products"
                                                            containerStyle={style?.qtyInputContainer}
                                                            inputContainerStyle={style?.qtyInputInner}
                                                            inputStyle={style?.qtyInputText}
                                                            renderErrorMessage={false}
                                                            onChangeText={(text) => setBulkQuantity(text.replace(/[^0-9]/g, ""))}
                                                        />
                                                    </View>
                                                    <ZenButton
                                                        title="Apply to all"
                                                        type="outline"
                                                        titleColor={theme?.primary}
                                                        outlineColor={theme?.primary}
                                                        buttonStyle={style?.outlineButton}
                                                        containerStyle={style?.buttonContainer}
                                                        disabled={busy !== null}
                                                        onPress={onApplyToAll}
                                                    />
                                                </View>
                                            </View>

                                            <FlatList
                                                data={rows}
                                                keyExtractor={(item) => labelRowKey(item)}
                                                renderItem={({ item }) => renderRow({ item, theme })}
                                                extraData={quantities}
                                                contentContainerStyle={style?.listContent}
                                                keyboardShouldPersistTaps="handled"
                                                showsVerticalScrollIndicator={false}
                                            />
                                        </>
                                    ) : (
                                        <View style={style?.previewWrapper}>
                                            <ZenWebView
                                                originWhitelist={["*"]}
                                                source={{ html }}
                                                style={style?.webView}
                                                startInLoadingState
                                                renderLoading={() => <ZenLoader size={"small"} color={theme?.primary} />}
                                            />
                                        </View>
                                    )}
                                </>
                            ) : (
                                <View style={style?.emptyWrapper}>
                                    <ZenIcon
                                        name="barcode-off"
                                        type="material-community"
                                        size={48}
                                        color={theme?.lightGrey}
                                    />
                                    <ZenText
                                        content="None of the selected products have a SKU, so there is nothing to print."
                                        size={theme?.smallerFontSize}
                                        color={theme?.grey}
                                        style={{ textAlign: "center" }}
                                    />
                                </View>
                            )}

                            {hasLabels && (
                                <View style={style?.totalsBar}>
                                    <ZenIcon
                                        name="barcode"
                                        type="material-community"
                                        size={theme?.iconSize}
                                        color={invalidCount > 0 ? theme?.red : theme?.primary}
                                    />
                                    <ZenText
                                        content={
                                            invalidCount > 0
                                                ? `${invalidCount} quantity field${invalidCount === 1 ? "" : "s"} need${invalidCount === 1 ? "s" : ""} a number between ${LABEL_QUANTITY_MIN} and ${LABEL_QUANTITY_MAX}`
                                                : `${rows.length} product${rows.length === 1 ? "" : "s"}, ${totalLabels} label${totalLabels === 1 ? "" : "s"}`
                                        }
                                        size={theme?.smallFontSize}
                                        color={invalidCount > 0 ? theme?.red : theme?.lightDark}
                                        style={{ flex: 1 }}
                                    />
                                </View>
                            )}

                            <View style={style?.footer}>
                                {/* Only needed when the download notification could not be posted */}
                                {savedFile?.location === "downloads" && (
                                    <ZenButton
                                        title="Open"
                                        type="outline"
                                        titleColor={theme?.primary}
                                        outlineColor={theme?.primary}
                                        buttonStyle={style?.outlineButton}
                                        containerStyle={[style?.buttonContainer, { flex: isLandscape ? 0 : 1 }]}
                                        disabled={busy !== null}
                                        onPress={onOpenSaved}
                                    />
                                )}
                                {/* Web: no PDF writer in a browser (html-to-pdf is a throwing shim); Print -> "Save as PDF" covers it. */}
                                {Platform.OS !== "web" && <ZenButton
                                    title="Download PDF"
                                    type="outline"
                                    titleColor={theme?.primary}
                                    outlineColor={theme?.primary}
                                    buttonStyle={style?.outlineButton}
                                    containerStyle={[style?.buttonContainer, { flex: isLandscape ? 0 : 1 }]}
                                    disabled={!canPrint}
                                    loading={busy === "download"}
                                    onPress={onDownload}
                                />}
                                <ZenButton
                                    title="Print"
                                    titleColor={theme?.light}
                                    buttonStyle={canPrint ? style?.primaryButton : style?.outlineButton}
                                    containerStyle={[style?.buttonContainer, { flex: isLandscape ? 0 : 1 }]}
                                    disabled={!canPrint}
                                    loading={busy === "print"}
                                    onPress={onPrint}
                                />
                            </View>
                        </View>

                    );
                }}
            </ThemeConsumer>
            <Toast config={ZenToastConfig} position='bottom' bottomOffset={30} />

        </CustomModal>
    );
};

export default React.memo(PrintProductLabels);
